import { useEffect, useState } from 'react';
import api from '../../services/api';
import PMModal from './PMModal';

const fmtDate = (d) => {
  if (!d) return '—';
  try { return new Date(d).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' }); } catch { return d; }
};

const statusColor = (s) => {
  if (s === 'Sent to PM')        return { bg: '#fef3c7', color: '#d97706' };
  if (s === 'Sent to HR')        return { bg: '#dbeafe', color: '#1d4ed8' };
  if (s === 'Notified Employee') return { bg: '#dcfce7', color: '#15803d' };
  if (s === 'PM Reviewed')       return { bg: '#dcfce7', color: '#16a34a' };
  return { bg: '#f1f5f9', color: '#64748b' };
};

function PMLostReportsPg() {
  const [reports, setReports] = useState([]);
  const [loading, setLoading] = useState(true);
  const [active, setActive] = useState(null);
  const [comment, setComment] = useState('');
  const [fineAmount, setFineAmount] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [toast, setToast] = useState('');

  const load = async () => {
    setLoading(true);
    try {
      const data = await api.getPMLostReports();
      setReports(Array.isArray(data) ? data : []);
    } catch { setReports([]); }
    setLoading(false);
  };

  useEffect(() => { load(); }, []);

  const showToast = (msg) => { setToast(msg); setTimeout(() => setToast(''), 3500); };

  const openReport = (r) => {
    setActive(r);
    setComment(r.pm_comment || '');
    setFineAmount(r.fine_amount ? String(r.fine_amount) : '');
  };

  const handleSubmitToHR = async () => {
    if (!comment.trim()) { alert('Please enter your review comment'); return; }
    if (!fineAmount || isNaN(parseFloat(fineAmount)) || parseFloat(fineAmount) < 0) {
      alert('Please enter a valid fine amount (0 if no fine)');
      return;
    }
    setSubmitting(true);
    try {
      await api.pmSubmitLostFine({
        reportId: active.report_id,
        comment:  comment.trim(),
        fineAmount: parseFloat(fineAmount),
      });
      showToast(`Review submitted. Fine ₹${parseFloat(fineAmount).toLocaleString('en-IN')} set. HR notified.`);
      setActive(null);
      await load();
    } catch (e) { alert(e.message); }
    setSubmitting(false);
  };

  // Download report as text
  const downloadReport = (r) => {
    const text = `
LOST ASSET REPORT
=================
Report ID   : ${r.report_id}
Date        : ${fmtDate(r.created_at)}

EMPLOYEE DETAILS
Employee    : ${r.employee_name || '—'}
Designation : ${r.employee_designation || '—'}

ASSET DETAILS
Asset       : ${r.asset_name || '—'}
Asset Tag   : ${r.asset_tag || '—'}
Asset Value : ₹${parseFloat(r.asset_value || 0).toFixed(2)}
Lost Date   : ${fmtDate(r.lost_date)}

FINE DETAILS
Fine Amount : ₹${parseFloat(r.fine_amount || 0).toFixed(2)}
Charged By  : ${r.it_admin_name || '—'}

DESCRIPTION
${r.message || '—'}

IT ADMIN COMMENT
${r.it_comment || '—'}

PM COMMENT
${r.pm_comment || 'Not yet reviewed'}

Status: ${r.status}
`.trim();

    const blob = new Blob([text], { type: 'text/plain' });
    const a = document.createElement('a');
    a.href = URL.createObjectURL(blob);
    a.download = `${r.report_id}.txt`;
    a.click();
    URL.revokeObjectURL(a.href);
  };

  const pending  = reports.filter(r => r.status === 'Sent to PM');
  const sentToHR = reports.filter(r => r.status === 'Sent to HR');
  const done     = reports.filter(r => r.status === 'Notified Employee' || r.status === 'PM Reviewed');

  return (
    <div>
      {/* Header */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 18 }}>
        <div>
          <h2 style={{ margin: 0, color: '#1e293b', fontSize: 20, fontWeight: 700 }}>🔴 Lost Asset Reports</h2>
          <p style={{ margin: '4px 0 0', color: '#64748b', fontSize: 13 }}>Review and comment on lost asset reports from IT Admin</p>
        </div>
        {pending.length > 0 && (
          <span style={{ background: '#dc2626', color: '#fff', borderRadius: 20, padding: '4px 12px', fontWeight: 700, fontSize: 13 }}>
            {pending.length} Pending
          </span>
        )}
      </div>

      {toast && (
        <div style={{ background: '#dcfce7', border: '1px solid #bbf7d0', color: '#15803d', borderRadius: 8, padding: '10px 14px', marginBottom: 14, fontWeight: 600, fontSize: 13 }}>
          ✅ {toast}
        </div>
      )}

      {loading ? (
        <div style={{ textAlign: 'center', padding: 60, color: '#94a3b8' }}>Loading reports...</div>
      ) : reports.length === 0 ? (
        <div style={{ textAlign: 'center', padding: 60, color: '#94a3b8' }}>
          <div style={{ fontSize: 48, marginBottom: 10 }}>📋</div>
          <div>No lost asset reports assigned to you.</div>
        </div>
      ) : (
        <>
          {/* Pending — Sent to PM */}
          {pending.length > 0 && (
            <div style={{ marginBottom: 24 }}>
              <div style={{ fontSize: 13, fontWeight: 700, color: '#dc2626', marginBottom: 10, textTransform: 'uppercase', letterSpacing: 0.5 }}>
                ⚠️ Pending Review ({pending.length})
              </div>
              <div style={{ display: 'grid', gap: 12 }}>
                {pending.map(r => <ReportCard key={r.id} r={r} onView={openReport} onDownload={downloadReport} />)}
              </div>
            </div>
          )}

          {/* Sent to HR — awaiting HR action */}
          {sentToHR.length > 0 && (
            <div style={{ marginBottom: 24 }}>
              <div style={{ fontSize: 13, fontWeight: 700, color: '#1d4ed8', marginBottom: 10, textTransform: 'uppercase', letterSpacing: 0.5 }}>
                📤 Forwarded to HR ({sentToHR.length})
              </div>
              <div style={{ display: 'grid', gap: 12 }}>
                {sentToHR.map(r => <ReportCard key={r.id} r={r} onView={openReport} onDownload={downloadReport} />)}
              </div>
            </div>
          )}

          {/* Done */}
          {done.length > 0 && (
            <div>
              <div style={{ fontSize: 13, fontWeight: 700, color: '#059669', marginBottom: 10, textTransform: 'uppercase', letterSpacing: 0.5 }}>
                ✅ Completed ({done.length})
              </div>
              <div style={{ display: 'grid', gap: 12 }}>
                {done.map(r => <ReportCard key={r.id} r={r} onView={openReport} onDownload={downloadReport} />)}
              </div>
            </div>
          )}
        </>
      )}

      {/* Detail / Comment Modal */}
      <PMModal open={!!active} onClose={() => setActive(null)} title={`Lost Asset Report — ${active?.report_id || ''}`}>
        {active && (
          <div>
            {/* Report Summary */}
            <div style={{ border: '1px solid #e2e8f0', borderRadius: 10, overflow: 'hidden', marginBottom: 14 }}>
              <div style={{ background: '#1e1b4b', color: '#fff', padding: '10px 14px', fontWeight: 700, fontSize: 13 }}>
                🔴 Asset Loss Details — {fmtDate(active.lost_date)}
              </div>
              <div style={{ padding: '12px 14px', display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '6px 16px' }}>
                <InfoRow label="Employee" val={active.employee_name} />
                <InfoRow label="Designation" val={active.employee_designation} />
                <InfoRow label="Asset Name" val={active.asset_name} />
                <InfoRow label="Asset Tag" val={active.asset_tag} />
                <InfoRow label="Asset Value" val={`₹${parseFloat(active.asset_value || 0).toFixed(2)}`} />
                <InfoRow label="Fine Amount" val={`₹${parseFloat(active.fine_amount || 0).toFixed(2)}`} accent="#dc2626" />
                <InfoRow label="IT Admin" val={active.it_admin_name} />
                <InfoRow label="Status" val={active.status} />
              </div>
              <div style={{ padding: '0 14px 12px' }}>
                <div style={{ background: '#fef2f2', borderRadius: 8, padding: '10px 12px', fontSize: 13, color: '#374151', lineHeight: 1.6 }}>
                  {active.message}
                </div>
              </div>
              {active.it_comment && (
                <div style={{ padding: '0 14px 12px' }}>
                  <div style={{ fontSize: 11, color: '#64748b', marginBottom: 4 }}>IT Admin Note:</div>
                  <div style={{ background: '#f1f5f9', borderRadius: 6, padding: '8px 10px', fontSize: 12, color: '#475569' }}>
                    {active.it_comment}
                  </div>
                </div>
              )}
            </div>

            {/* Download button */}
            <button
              onClick={() => downloadReport(active)}
              style={{ width: '100%', padding: '8px', background: '#f8fafc', border: '1px solid #e2e8f0', borderRadius: 8, fontSize: 13, color: '#4f46e5', fontWeight: 600, cursor: 'pointer', marginBottom: 14 }}>
              📥 Download Report
            </button>

            {/* PM Comment + Fine Amount */}
            {active.status !== 'Sent to HR' && active.status !== 'Notified Employee' ? (
              <>
                {/* Fine Amount */}
                <div style={{ marginBottom: 14 }}>
                  <label style={{ fontSize: 13, fontWeight: 700, color: '#dc2626', display: 'block', marginBottom: 6 }}>
                    💰 Fine Amount (₹) *
                  </label>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                    <span style={{ fontSize: 16, fontWeight: 700, color: '#64748b' }}>₹</span>
                    <input
                      type="number"
                      min="0"
                      step="0.01"
                      value={fineAmount}
                      onChange={e => setFineAmount(e.target.value)}
                      placeholder="Enter fine amount (0 if none)"
                      style={{ flex: 1, padding: '9px 12px', border: '2px solid #dc2626', borderRadius: 8, fontSize: 14, fontWeight: 700, color: '#1e293b', outline: 'none' }}
                    />
                  </div>
                  <div style={{ fontSize: 11, color: '#64748b', marginTop: 4 }}>
                    Enter 0 if no fine is applicable. This amount will be sent to HR and notified to the employee.
                  </div>
                </div>

                {/* Comment */}
                <div style={{ marginBottom: 14 }}>
                  <label style={{ fontSize: 13, fontWeight: 600, color: '#374151', display: 'block', marginBottom: 6 }}>
                    📝 Review Comment *
                  </label>
                  <textarea
                    rows={3}
                    value={comment}
                    onChange={e => setComment(e.target.value)}
                    placeholder="Add your review comments about this lost asset incident..."
                    style={{ width: '100%', padding: '8px 10px', border: '1px solid #d1d5db', borderRadius: 8, fontSize: 13, resize: 'vertical', boxSizing: 'border-box' }}
                  />
                </div>

                <div style={{ display: 'flex', gap: 10, justifyContent: 'flex-end' }}>
                  <button onClick={() => setActive(null)}
                    style={{ padding: '9px 18px', border: '1px solid #d1d5db', borderRadius: 8, background: '#fff', cursor: 'pointer', fontSize: 13 }}>
                    Close
                  </button>
                  <button onClick={handleSubmitToHR} disabled={submitting}
                    style={{ padding: '9px 20px', background: '#dc2626', color: '#fff', border: 'none', borderRadius: 8, fontWeight: 700, cursor: 'pointer', fontSize: 13 }}>
                    {submitting ? 'Submitting...' : '📤 Set Fine & Send to HR'}
                  </button>
                </div>
              </>
            ) : (
              <div style={{ background: '#dcfce7', borderRadius: 8, padding: '12px 14px', fontSize: 13, color: '#15803d', fontWeight: 500, textAlign: 'center' }}>
                ✅ Report reviewed. Fine ₹{parseFloat(active.fine_amount || 0).toLocaleString('en-IN')} set. Forwarded to HR.
                <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: 10 }}>
                  <button onClick={() => setActive(null)}
                    style={{ padding: '7px 16px', border: '1px solid #bbf7d0', borderRadius: 8, background: '#fff', cursor: 'pointer', fontSize: 13, color: '#15803d', fontWeight: 600 }}>
                    Close
                  </button>
                </div>
              </div>
            )}
          </div>
        )}
      </PMModal>
    </div>
  );
}

function ReportCard({ r, onView, onDownload }) {
  const sc = statusColor(r.status);
  return (
    <div style={{ border: '1px solid #e2e8f0', borderRadius: 10, padding: '14px 16px', background: '#fff', boxShadow: '0 1px 4px rgba(0,0,0,.05)' }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 10 }}>
        <div>
          <div style={{ fontWeight: 700, color: '#1e293b', fontSize: 14 }}>
            {r.asset_name} <span style={{ fontWeight: 400, color: '#64748b', fontSize: 12 }}>({r.asset_id})</span>
          </div>
          <div style={{ fontSize: 12, color: '#64748b', marginTop: 2 }}>Report: {r.report_id} · {fmtDate(r.created_at)}</div>
        </div>
        <span style={{ background: sc.bg, color: sc.color, borderRadius: 20, padding: '3px 10px', fontSize: 11, fontWeight: 700, whiteSpace: 'nowrap' }}>
          {r.status}
        </span>
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3,1fr)', gap: 8, marginBottom: 12 }}>
        <MiniCard label="Employee" val={r.employee_name} />
        <MiniCard label="Fine Amount" val={`₹${parseFloat(r.fine_amount || 0).toLocaleString('en-IN')}`} accent="#dc2626" />
        <MiniCard label="Lost Date" val={fmtDate(r.lost_date)} />
      </div>

      <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
        <button
          onClick={() => onDownload(r)}
          style={{ padding: '5px 12px', border: '1px solid #e2e8f0', background: '#f8fafc', borderRadius: 6, fontSize: 12, cursor: 'pointer', color: '#4f46e5', fontWeight: 600 }}>
          📥 Download
        </button>
        <button
          onClick={() => onView(r)}
          style={{ padding: '5px 14px', background: r.status === 'Sent to PM' ? '#dc2626' : '#f1f5f9', color: r.status === 'Sent to PM' ? '#fff' : '#374151', border: 'none', borderRadius: 6, fontSize: 12, cursor: 'pointer', fontWeight: 600 }}>
          {r.status === 'Sent to PM' ? '📋 Review & Set Fine' : '👁 View'}
        </button>
      </div>
    </div>
  );
}

function InfoRow({ label, val, accent }) {
  return (
    <div style={{ display: 'flex', flexDirection: 'column', paddingBottom: 4 }}>
      <span style={{ fontSize: 10, color: '#94a3b8', textTransform: 'uppercase', letterSpacing: 0.4 }}>{label}</span>
      <span style={{ fontSize: 13, fontWeight: 600, color: accent || '#1e293b' }}>{val || '—'}</span>
    </div>
  );
}

function MiniCard({ label, val, accent }) {
  return (
    <div style={{ background: '#f8fafc', borderRadius: 6, padding: '6px 8px' }}>
      <div style={{ fontSize: 10, color: '#94a3b8', marginBottom: 2 }}>{label}</div>
      <div style={{ fontSize: 12, fontWeight: 700, color: accent || '#374151' }}>{val || '—'}</div>
    </div>
  );
}

export default PMLostReportsPg;
